// O que o INSCRITO pode ver sobre a própria inscrição.
// Toda a decisão de sigilo vem da função situacao_para_inscrito:
// - sigilo = false → situação real (pendente, confirmada, isenta, lista_espera,
//   cancelada), valores e cobranças como sempre foram.
// - sigilo = true  → 'recebida', sem valores e sem revelar vaga/suplente.
// A direção continua vendo tudo pelo painel — isto é só para o participante.

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

export type SituacaoParticipante =
  | 'recebida'
  | 'pendente'
  | 'confirmada'
  | 'isenta'
  | 'lista_espera'
  | 'cancelada';

export interface SituacaoInscrito {
  situacao: SituacaoParticipante;
  sigilo: boolean;
  mostrar_valores: boolean;
  texto?: string | null;
}

export const TEXTO_INSCRICAO_RECEBIDA =
  'Inscrição recebida. A direção vai entrar em contato com você.';

/** Selo neutro usado antes da resposta e quando a inscrição é sigilosa. */
export function situacaoNeutra(): SituacaoInscrito {
  return {
    situacao: 'recebida',
    sigilo: true,
    mostrar_valores: false,
    texto: TEXTO_INSCRICAO_RECEBIDA,
  };
}

export async function buscarSituacaoInscrito(
  inscricaoId: number,
): Promise<SituacaoInscrito | null> {
  const { data, error } = await supabase.rpc('situacao_para_inscrito', {
    p_inscricao: inscricaoId,
  });
  if (error) throw error;
  if (!data) return null;
  const bruto = data as Partial<SituacaoInscrito>;
  if (!bruto.situacao) return null;
  return {
    situacao: bruto.situacao,
    sigilo: bruto.sigilo === true,
    mostrar_valores: bruto.mostrar_valores !== false,
    texto: bruto.texto ?? null,
  };
}

/**
 * Busca a situação de várias inscrições em paralelo.
 * Enquanto não chega resposta, o mapa não tem a chave — então a tela deve
 * mostrar o selo neutro, nunca o status real antes da hora.
 */
export function useSituacoesInscrito(ids: number[]): {
  situacoes: Map<number, SituacaoInscrito>;
  carregando: boolean;
} {
  const chave = useMemo(
    () => [...new Set(ids)].filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b).join(','),
    [ids],
  );
  const [situacoes, setSituacoes] = useState<Map<number, SituacaoInscrito>>(new Map());
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    const lista = chave ? chave.split(',').map(Number) : [];

    if (lista.length === 0) {
      setSituacoes(new Map());
      setCarregando(false);
      return () => {
        ativo = false;
      };
    }

    setCarregando(true);
    Promise.all(
      lista.map(async (id) => {
        try {
          const s = await buscarSituacaoInscrito(id);
          return [id, s] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((res) => {
      if (!ativo) return;
      const mapa = new Map<number, SituacaoInscrito>();
      res.forEach(([id, s]) => {
        if (s) mapa.set(id, s);
      });
      setSituacoes(mapa);
      setCarregando(false);
    });

    return () => {
      ativo = false;
    };
  }, [chave]);

  return { situacoes, carregando };
}